class GinSummerVideo extends HTMLElement {
  connectedCallback() {
    this.container = this.querySelector(".relative");
    this.full = this.querySelector('video[data-role="full"]');
    this.teaser = this.querySelector('video[data-role="teaser"]');
    this.btn = this.querySelector('[data-role="play-btn"]');
    this.progress = this.querySelector('[data-role="progress"]');
    this.overlay = this.querySelector('[data-role="overlay"]');

    this.full.style.opacity = "0";
    this.full.style.transition = "opacity 150ms linear";

    this._boundToggle = this.toggle.bind(this);
    this.overlay.addEventListener("click", this._boundToggle);
    this.btn.addEventListener("click", this._boundToggle);

    this.full.addEventListener("play", () => this._showBtn(false));
    this.full.addEventListener("pause", () => this._showBtn(true));

    this._onTime = () => this._updateProgress();
    this._onEnded = () => this._resetAfterEnd();
    this.full.addEventListener("timeupdate", this._onTime);
    this.full.addEventListener("ended", this._onEnded);

    this._onPlay = () => this._startProgressLoop();
    this._onPause = () => this._stopProgressLoop();
    this.full.addEventListener("play", this._onPlay);
    this.full.addEventListener("pause", this._onPause);

    this.progressWrapper = this.querySelector(".progress-wrapper");
    this.progressWrapper.style.opacity = "0";
    this.progressWrapper.style.transition = "opacity 150ms linear";

    this._io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => e.isIntersecting && this._ensureLoaded()),
      { rootMargin: "200px" }
    );
    this._io.observe(this);

    this._showBtn(true);
    this._setProgress(0);
  }

  disconnectedCallback() {
    this.overlay?.removeEventListener("click", this._boundToggle);
    this.btn?.removeEventListener("click", this._boundToggle);
    this.full?.removeEventListener("timeupdate", this._onTime);
    this.full?.removeEventListener("ended", this._onEnded);
    this.full?.removeEventListener("play", this._onPlay);
    this.full?.removeEventListener("pause", this._onPause);
    this._io?.disconnect();
    this._stopProgressLoop();
  }

  _ensureLoaded() {
    const sources = Array.from(this.full.querySelectorAll("source"));
    const hasDataSources = sources.some((s) => s.dataset.src);
    if (hasDataSources) {
      sources.forEach((s) => {
        if (s.dataset.src) {
          s.setAttribute("src", s.dataset.src);
          s.removeAttribute("data-src");
        }
      });
      this.full.removeAttribute("data-src");
      this.full.load();
    } else if (!this.full.getAttribute("src") && this.full.dataset.src) {
      this.full.setAttribute("src", this.full.dataset.src);
      this.full.removeAttribute("data-src");
      this.full.load();
    }
  }

  _waitForCanPlay() {
    return new Promise((resolve) => {
      if (this.full.readyState >= 3) return resolve();
      const onReady = () => {
        this.full.removeEventListener("canplay", onReady);
        this.full.removeEventListener("loadeddata", onReady);
        resolve();
      };
      this.full.addEventListener("canplay", onReady, { once: true });
      this.full.addEventListener("loadeddata", onReady, { once: true });

      setTimeout(() => resolve(), 1500);
    });
  }

  async toggle() {
    if (!this.full.paused && !this.full.ended) {
      this.full.pause();
      return;
    }

    this._ensureLoaded();
    await this._waitForCanPlay();

    if (this.teaser) {
      this.teaser.style.transition = "opacity 150ms linear";
      this.teaser.style.opacity = "0";
      try {
        this.teaser.pause?.();
      } catch {}
    }
    this.full.style.opacity = "1";

    try {
      await this.full.play();
    } catch (e) {
      this._showBtn(true);
      if (this.teaser) this.teaser.style.opacity = "1";
    }
  }

  _startProgressLoop() {
    this.progressWrapper.style.opacity = "1";
    if (this._raf) return;
    const tick = () => {
      this._updateProgress();
      if (!this.full.paused && !this.full.ended) {
        this._raf = requestAnimationFrame(tick);
      } else {
        this._raf = null;
      }
    };
    this._raf = requestAnimationFrame(tick);
  }

  _stopProgressLoop() {
    if (this._raf) {
      cancelAnimationFrame(this._raf);
      this._raf = null;
    }
    this.progressWrapper.style.opacity = "0";
  }

  _showBtn(show) {
    this.btn.style.opacity = show ? "1" : "0";
    this.btn.style.pointerEvents = show ? "auto" : "none";
  }

  _updateProgress() {
    if (isFinite(this.full.duration) && this.full.duration > 0) {
      const pct = (this.full.currentTime / this.full.duration) * 100;
      this._setProgress(pct);
    }
  }

  _setProgress(pct) {
    this.progress.style.width = `${Math.max(0, Math.min(pct, 100))}%`;
  }

  _resetAfterEnd() {
    this._stopProgressLoop();
    this._setProgress(0);
    this._showBtn(true);

    this.progressWrapper.style.opacity = "0";

    if (this.teaser) {
      this.teaser.style.transition = "opacity 150ms linear";
      this.teaser.style.opacity = "1";
      try {
        this.teaser.play?.();
      } catch {}
    }

    this.full.currentTime = 0;
    this.full.pause();
    this.full.style.opacity = "0";
  }
}

customElements.define("gin-summer-video", GinSummerVideo);
